import type { AdvanceTableConfig } from '@aibees/shared/features';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { useBoolean, useResponsive } from '@aibees/shared/hooks';
import type { SxStyle } from '@aibees/shared/theme';
import { useIsInternalUser } from '@aibees/shared/workspace/hooks/use-is-internal-user';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  LabelDisplayedRowsArgs,
  Pagination,
  Stack,
  TablePagination,
  TextField,
  Typography,
  type TablePaginationProps,
} from '@mui/material';
import { min } from 'lodash-es';
import { memo, useMemo, useState } from 'react';
import { BulkLimitDialog } from '../bulk-limit-dialog';
import { SkeletonList } from '../skeleton-list';
import * as SX from './sx';
import { TablePageSelector } from './table-page-selector';

type Props = {
  config: AdvanceTableConfig;
  dense?: boolean;
  isLoading?: boolean;
  sx?: SxStyle;
  onChangeDense?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const TablePaginationCustom = memo(
  ({
    config,
    dense,
    onChangeDense,
    sx,
    isLoading,
    ...other
  }: Props & TablePaginationProps) => {
    const { data } = useCurrentSubscriptionApi();
    const internalUser = useIsInternalUser();

    const limitDialogState = useBoolean();
    const lgUp = useResponsive('up', 'lg');
    const [jumpToPage, setJumpToPage] = useState<number>();
    const totalPages = useMemo(
      () =>
        min([
          config.pageSelectorMax,
          Math.ceil(other.count / other.rowsPerPage),
        ]) || 1,
      [other.count, other.rowsPerPage, config.pageSelectorMax]
    );
    const limitPagination =
      config.paidPagination &&
      data?.current?.plan?.metadata.is_free &&
      !internalUser;

    const handleJumpToPage = () => {
      if (limitPagination) {
        limitDialogState.onTrue();
        return;
      }

      if (!!jumpToPage && jumpToPage > 0 && jumpToPage <= totalPages) {
        other.onPageChange(null, jumpToPage - 1);
        setJumpToPage(undefined);
      }
    };

    const handleSelectPage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      page: number
    ) => {
      if (limitPagination) {
        limitDialogState.onTrue();
        return;
      }
      other.onPageChange(event, page);
    };

    const handlePagination = (
      event: React.ChangeEvent<unknown>,
      newPage: number
    ) => {
      if (limitPagination) {
        limitDialogState.onTrue();
        return;
      }
      other.onPageChange(
        event as React.MouseEvent<HTMLButtonElement, MouseEvent>,
        newPage - 1
      );
    };

    return (
      <Box
        className="TablePaginationCustom"
        sx={{ ...SX.paginationWrapper, ...sx } as SxStyle}
      >
        <BulkLimitDialog
          state={limitDialogState}
          imgSrc="/assets/images/upgradeFullSearch.png"
          title="Unlock Full Search Results with a Paid Plan"
        />
        {isLoading ? (
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ height: (p) => p.spacing(5.5) }}
          >
            <SkeletonList
              count={[
                { variant: 'rounded', width: 50, height: 25 },
                { variant: 'rounded', width: 140, height: 25 },
                { variant: 'rounded', width: 200, height: 25 },
              ]}
              sx={{
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
              }}
            />
            {config.showJumpToPage && (
              <SkeletonList
                count={[
                  { variant: 'rounded', width: 85, height: 25 },
                  { variant: 'rounded', width: 40, height: 25 },
                  { variant: 'rounded', width: 44, height: 25 },
                ]}
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 1,
                }}
              />
            )}
          </Stack>
        ) : (
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ height: (p) => p.spacing(5.5) }}
          >
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="start"
            >
              <Stack justifyContent="center" alignItems="center">
                {config.showPageSelector !== false && (
                  <TablePageSelector
                    current={other.page}
                    count={totalPages}
                    onChange={handleSelectPage}
                  />
                )}
              </Stack>
              <TablePagination
                rowsPerPageOptions={config.rowsPerPageOptions}
                component="div"
                {...other}
                nextIconButtonProps={
                  config.pageSelectorMax &&
                  other.page + 1 >= config.pageSelectorMax
                    ? {
                        disabled: true,
                      }
                    : {}
                }
                sx={
                  {
                    ...SX.tablePagination,
                    ...{
                      '& [class*="MuiTablePagination-select"]': {
                        display: config.showRowsPerPage ? undefined : 'none',
                      },
                      '& [class*="MuiTablePagination-input"]': {
                        display: config.showRowsPerPage ? undefined : 'none',
                      },
                    },
                  } as SxStyle
                }
                labelDisplayedRows={(
                  paginationInfo: LabelDisplayedRowsArgs
                ) => (
                  <>
                    {`${paginationInfo.from}-${paginationInfo.to} of `}{' '}
                    <b>{paginationInfo.count}</b> Results
                  </>
                )}
              />
              <Pagination
                count={totalPages}
                page={other.page + 1}
                onChange={handlePagination}
                siblingCount={1}
                boundaryCount={1}
                sx={SX.pagination}
                shape="rounded"
              />
            </Stack>
            {config.showJumpToPage && lgUp && (
              <Stack
                flexDirection="row"
                alignItems="center"
                justifyContent="start"
                gap={1.25}
              >
                <Typography
                  variant="body2"
                  sx={{ color: (p) => p.palette.common.black }}
                >
                  Jump to page
                </Typography>
                <TextField
                  size="small"
                  autoComplete="off"
                  type="number"
                  value={jumpToPage}
                  inputProps={{ min: 1, max: totalPages }}
                  onChange={(e) =>
                    setJumpToPage(
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      handleJumpToPage();
                      ev.preventDefault();
                    }
                  }}
                  error={
                    !!jumpToPage && (jumpToPage < 0 || jumpToPage > totalPages)
                  }
                  sx={SX.jumpToPageInput}
                />
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleJumpToPage}
                  size="small"
                >
                  Go
                </LoadingButton>
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    );
  }
);

TablePaginationCustom.displayName = 'TablePaginationCustom';
